import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { formatBytes } from "@/lib/format";
import { useUploader } from "./useUploader";
import { ExpirationSelector } from "./ExpirationSelector";
import { SuccessCard } from "./SuccessCard";
import { EmptyDropzone, UploadList, UploadTabs, UPLOAD_TABS } from "./UploadParts";

const LOCKED_MESSAGE = "Need longer storage? Create a free account.";
const LOCKED_ACTION = { label: "Register", onClick: () => (window.location.href = "/register") };

function useExpirationOptions(authed, settings) {
  return useMemo(() => {
    const options = authed ? settings.auth_expiration_options : settings.anon_expiration_options;
    const shown = authed ? options : [...options, ...(options.includes("unlimited") ? [] : ["unlimited"])];
    return { shown, locked: authed ? [] : shown.filter((o) => !options.includes(o)), defaultExp: authed ? settings.auth_default_expiration : settings.anon_default_expiration };
  }, [authed, settings]);
}

export function UploadCard() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const authed = !!user;
  const { shown, locked, defaultExp } = useExpirationOptions(authed, settings);
  const maxSize = authed ? settings.auth_max_file_size : settings.anon_max_file_size;
  const [expiration, setExpiration] = useState(defaultExp);
  const [tab, setTab] = useState("file");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const { items, addFiles, remove, cancel, upload, reset } = useUploader();

  useEffect(() => setExpiration(defaultExp), [defaultExp]);

  const results = useMemo(() => items.filter((i) => i.status === "done").map((i) => i.result), [items]);
  const allDone = items.length > 0 && items.every((i) => i.status === "done");
  const canSubmit = items.some((i) => i.status === "pending" || i.status === "error");

  const pick = (files) => {
    if (!files?.length) return;
    const list = settings.multiple_upload_enabled ? Array.from(files) : Array.from(files).slice(0, 1);
    const tooBig = list.filter((f) => f.size > maxSize);
    if (tooBig.length) toast.error(`${tooBig.length === 1 ? tooBig[0].name + " is" : tooBig.length + " files are"} larger than ${formatBytes(maxSize)}.`);
    const ok = list.filter((f) => f.size <= maxSize && f.size > 0);
    if (ok.length) addFiles(ok);
  };

  const start = async () => {
    setBusy(true);
    let done = 0;
    for (const item of items.filter((i) => i.status === "pending" || i.status === "error")) {
      if (await upload(item, expiration)) done += 1;
    }
    setBusy(false);
    if (done) toast.success(done === 1 ? "Upload complete" : `${done} files uploaded`);
  };

  if (allDone) return <SuccessCard results={results} onReset={reset} />;

  const openPicker = () => inputRef.current?.click();
  const accept = UPLOAD_TABS.find((t) => t.key === tab)?.accept;
  return (
    <div className="animate-rise" data-testid="upload-card">
      <UploadTabs value={tab} onChange={setTab} />
      <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }} onClick={() => !items.length && openPicker()}
        className={`relative rounded-2xl border-2 border-dashed p-6 text-center transition-all duration-200 sm:p-10 ${dragging ? "scale-[1.01] border-indigo-600 bg-indigo-100/50 shadow-lg" : "border-indigo-200 bg-indigo-50/30 hover:border-indigo-400 hover:bg-indigo-50/60"} ${items.length ? "" : "cursor-pointer"}`}
        data-testid="upload-dropzone">
        <input ref={inputRef} type="file" className="sr-only" multiple={settings.multiple_upload_enabled} accept={accept} onChange={(e) => { pick(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" aria-label="Choose files" />
        {items.length === 0 ? <EmptyDropzone settings={settings} maxSize={maxSize} onChoose={openPicker} /> : (
          <UploadList items={items} busy={busy} allowMore={settings.multiple_upload_enabled} onRemove={remove} onCancel={cancel} onRetry={(item) => upload(item, expiration)} onAddMore={openPicker} />
        )}
      </div>

      <p className="mt-6 text-center text-xs font-medium uppercase tracking-wider text-slate-400">Expires after</p>
      <div className="mt-3">
        <ExpirationSelector options={shown} value={expiration} onChange={setExpiration} lockedOptions={locked} onLockedClick={() => toast(LOCKED_MESSAGE, { action: LOCKED_ACTION })} />
      </div>
      {!authed && <p className="mt-4 text-center text-sm text-slate-500" data-testid="no-account-text">{settings.no_account_text} <Link to="/register" className="text-indigo-600 hover:underline" data-testid="need-longer-link">Need longer storage?</Link></p>}

      {items.length > 0 && (
        <div className="mt-6 flex justify-center">
          <Button onClick={start} disabled={busy || !canSubmit} className="h-12 w-full rounded-full bg-indigo-600 px-8 text-base font-semibold hover:bg-indigo-700 sm:w-auto" data-testid="upload-submit-btn">
            {busy ? "Uploading…" : `${settings.cta_upload} ${items.length > 1 ? `${items.length} files` : ""}`}
          </Button>
        </div>
      )}
    </div>
  );
}
